import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { messages } from './messages'
import type { MessageKey } from './messages'
import { readStoredLang, resolveInitialLang, writeStoredLang } from './language'
import type { Lang } from './language'

interface I18n {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: MessageKey) => string
  /** Picks the right side of a reference-data row without the caller thinking about language. */
  pick: (row: { nameZh: string; nameEn: string }) => string
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() =>
    resolveInitialLang(readStoredLang(), navigator.languages ?? [navigator.language]),
  )

  useEffect(() => {
    // Screen readers and the browser's own translation prompt both read this.
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en-NZ'
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    writeStoredLang(next)
  }, [])

  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang,

      // A missing key shows the key. Silent fallback to the other language would hide the gap
      // exactly where it needs to be seen.
      t: (key) => messages[key]?.[lang] ?? key,

      pick: (row) => (lang === 'zh' ? row.nameZh : row.nameEn),
    }),
    [lang, setLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const value = useContext(I18nContext)

  if (!value) {
    throw new Error('useI18n was called outside I18nProvider.')
  }

  return value
}
